/**
 * urheber.ts — „Angelegt von" im Team (DRPX-Pro, Teil 2b, 17.10.2026)
 *
 * Die Datenbank merkt sich in der Tabelle `urheber` (Migration 138), welcher
 * Zugang einer Firma einen Auftrag angelegt, ein Gebot abgegeben oder ein
 * Foto/Dokument hochgeladen hat. Lesen kann das nur die eigene Firma.
 * Dort steht nur die Kennung (`person_id`); die Namen liefert
 * `get_team_namen()` (Migration 142).
 *
 * Diese Datei enthält nur reine Hilfsfunktionen (ohne Datenbank), damit sie
 * sich einfach testen lassen. Laden: `hooks/useUrheber.ts`.
 *
 * @module lib/urheber
 */

import type { TranslateFn } from '../types.js';

/**
 * Wovon wollen wir wissen, wer es angelegt hat? `beladung`/`lieferschein`
 * (Migration 143): Kennung ist die des AUFTRAGS, gemeint ist, wer dessen
 * Beladungsfoto bzw. Lieferschein hochgeladen hat.
 */
export type UrheberArt = 'auftrag' | 'angebot' | 'foto' | 'dokument' | 'beladung' | 'lieferschein';

/** Spalte in `urheber` je Art. */
export const URHEBER_SPALTE: Record<
  UrheberArt,
  'auftrag_id' | 'angebot_id' | 'foto_id' | 'dokument_id' | 'beladung_auftrag_id' | 'lieferschein_auftrag_id'
> = {
  auftrag: 'auftrag_id',
  angebot: 'angebot_id',
  foto: 'foto_id',
  dokument: 'dokument_id',
  beladung: 'beladung_auftrag_id',
  lieferschein: 'lieferschein_auftrag_id',
};

/** Ein Zugang der eigenen Firma (aus `get_team_namen()`). */
export interface TeamName {
  vorname: string;
  nachname: string;
  /** Firmenkonto selbst (Inhaber) */
  istChef: boolean;
}

/**
 * Anzeigename für „Angelegt von".
 *
 * @param personId Kennung aus `urheber.person_id`; `null` = Zugang wurde
 *                 inzwischen entfernt (Fremdschlüssel `ON DELETE SET NULL`)
 * @param namen    Namen des Teams, Schlüssel = Kennung
 * @param t        Übersetzungsfunktion
 * @returns Name; ohne eingetragenen Namen „Inhaber" bzw. „Mitarbeiter";
 *          entfernt → „Ehemaliger Mitarbeiter"; unbekannte Kennung → '' (nichts anzeigen)
 */
export function urheberAnzeige(
  personId: string | null | undefined,
  namen: Record<string, TeamName>,
  t: TranslateFn,
): string {
  if (personId === null) return t('urheberEhemalig');
  if (!personId) return '';
  const n = namen[personId];
  if (!n) return '';
  const voll = [n.vorname, n.nachname].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  if (voll) return voll;
  return n.istChef ? t('hcInhaberLabel') : t('mitarbeiterLabel');
}

/**
 * Kennungen, zu denen noch kein Name bekannt ist (z. B. ein neuer
 * Mitarbeiter seit dem letzten Laden). `null` (entfernt) zählt nicht.
 */
export function unbekanntePersonen(
  personen: Record<string, string | null>,
  namen: Record<string, TeamName>,
): string[] {
  const fehlend = new Set<string>();
  Object.values(personen).forEach((p) => {
    if (p && !namen[p]) fehlend.add(p);
  });
  return Array.from(fehlend);
}

/** Liste in Stücke teilen (Adresslänge bei `in.(…)`-Abfragen begrenzen). */
export function inStuecken<T>(liste: T[], groesse: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < liste.length; i += groesse) out.push(liste.slice(i, i + groesse));
  return out;
}
